import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Alert,
  Skeleton,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccountBalance as AccountBalanceIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';

import { getPurchaseOrder, changePurchaseOrderStatus, updatePurchaseOrderItemCosts } from '../../collections/purchaseOrders';
import POReturnsSection from './POReturnsSection';

const PurchaseOrderDetail = ({ controlRole }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Cost editing state
  const [editingCosts, setEditingCosts] = useState(false);
  const [editedCosts, setEditedCosts] = useState({});
  const [savingCosts, setSavingCosts] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  const brandColors = {
    primary: '#0e2a04',
    secondary: '#6b8e4e',
    accent: '#d4a574',
    light: '#c8d6b8',
    background: '#f5f8f3',
  };

  const statusColors = {
    DRAFT: { bg: '#f4f1e8', color: '#8b7355' },
    SENT: { bg: '#e8f2e3', color: '#6b8e4e' },
    PARTIALLY_RECEIVED: { bg: '#f9f0e3', color: '#d4a574' },
    FULLY_RECEIVED: { bg: '#d8e8d2', color: '#1a4f0a' },
    CLOSED: { bg: '#c8d6b8', color: '#0e2a04' },
    CANCELLED: { bg: '#f0ede8', color: '#8b7355' },
  };

  const statusLabels = {
    DRAFT: 'Nháp',
    SENT: 'Đã gửi',
    PARTIALLY_RECEIVED: 'Nhận một phần',
    FULLY_RECEIVED: 'Nhận đầy đủ',
    CLOSED: 'Đã đóng',
    CANCELLED: 'Đã hủy',
  };

  const financialStatusColors = {
    UNPAID: { bg: '#fff3e0', color: '#e65100' },
    PAID: { bg: '#e8f5e9', color: '#2e7d32' },
  };

  const financialStatusLabels = {
    UNPAID: 'Chưa thanh toán',
    PAID: 'Đã thanh toán',
  };

  useEffect(() => {
    const loadPurchaseOrder = async () => {
      setLoading(true);
      setError('');
      
      try {
        const po = await getPurchaseOrder(id);
        console.log('Raw PO object:', po);
        console.log('Available transitions:', po?.available_transitions);
        console.log('Status:', po?.status);
        setPurchaseOrder(po);
      } catch (err) {
        console.error('Error loading purchase order:', err);
        setError('Không thể tải thông tin đơn đặt hàng. Vui lòng thử lại.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadPurchaseOrder();
    }
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Bạn có chắc chắn muốn chuyển trạng thái sang ${statusLabels[newStatus]}?`)) {
      return;
    }

    setChangingStatus(true);
    setError('');
    
    try {
      await changePurchaseOrderStatus(id, newStatus);
      // Reload the purchase order to get updated data
      const updatedPO = await getPurchaseOrder(id);
      setPurchaseOrder(updatedPO);
    } catch (err) {
      console.error('Error changing status:', err);
      setError('Không thể thay đổi trạng thái. Vui lòng thử lại.');
    } finally {
      setChangingStatus(false);
    }
  };

  const getStatusActionButton = (targetStatus, label, color = 'primary') => {
    // Backend returns transitions in uppercase format (e.g., "SENT", "CANCELLED")
    if (!purchaseOrder?.available_transitions?.includes(targetStatus)) {
      return null;
    }

    return (
      <Button
        variant="contained"
        onClick={() => handleStatusChange(targetStatus)}
        disabled={changingStatus}
        sx={{
          backgroundColor: color === 'primary' ? brandColors.primary : brandColors.secondary,
          '&:hover': {
            backgroundColor: color === 'primary' ? brandColors.secondary : brandColors.primary,
          }
        }}
      >
        {changingStatus ? 'Đang xử lý...' : label}
      </Button>
    );
  };

  const StatusChip = ({ status }) => {
    // Handle both "DRAFT" and "Draft" formats
    const normalizedStatus = status?.toString().toUpperCase();
    const colors = statusColors[normalizedStatus] || { bg: '#f0f0f0', color: '#666' };
    const label = statusLabels[normalizedStatus] || status;

    return (
      <Chip
        label={label}
        sx={{
          backgroundColor: colors.bg,
          color: colors.color,
          fontWeight: 600,
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '0.875rem',
          px: 2,
          py: 1,
        }}
      />
    );
  };

  const FinancialStatusChip = ({ status }) => {
    const normalizedStatus = status?.toString().toUpperCase();
    const colors = financialStatusColors[normalizedStatus] || { bg: '#f0f0f0', color: '#666' };
    const label = financialStatusLabels[normalizedStatus] || status;

    return (
      <Chip
        label={label}
        sx={{
          backgroundColor: colors.bg,
          color: colors.color,
          fontWeight: 600,
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '0.875rem',
          px: 2,
          py: 1,
        }}
      />
    );
  };

  const toggleRowExpansion = (productId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleReturnCreated = async () => {
    // Reload the purchase order to get updated total_amount
    try {
      const updatedPO = await getPurchaseOrder(id);
      setPurchaseOrder(updatedPO);
    } catch (err) {
      console.error('Error reloading PO after return:', err);
    }
  };

  // Cost editing handlers
  const handleStartEditCosts = () => {
    const initialCosts = {};
    purchaseOrder?.items?.forEach((item) => {
      initialCosts[item.id] = item.unit_cost || 0;
    });
    setEditedCosts(initialCosts);
    setEditingCosts(true);
  };

  const handleCancelEditCosts = () => {
    setEditingCosts(false);
    setEditedCosts({});
  };

  const handleCostChange = (itemId, value) => {
    setEditedCosts((prev) => ({
      ...prev,
      [itemId]: parseFloat(value) || 0,
    }));
  };

  const handleSaveCosts = async () => {
    try {
      setSavingCosts(true);
      const items = Object.entries(editedCosts).map(([itemId, unitCost]) => ({
        id: parseInt(itemId),
        unit_cost: unitCost,
      }));
      await updatePurchaseOrderItemCosts(id, items);
      // Reload PO to get updated data
      const updatedPO = await getPurchaseOrder(id);
      setPurchaseOrder(updatedPO);
      setEditingCosts(false);
      setEditedCosts({});
    } catch (err) {
      console.error('Error saving costs:', err);
      setError('Không thể lưu đơn giá. Vui lòng thử lại.');
    } finally {
      setSavingCosts(false);
    }
  };

  const getEditedReceivedTotal = (item) => {
    const unitCost = editedCosts[item.id] ?? item.unit_cost;
    return (item.quantity_received || 0) * unitCost;
  };

  const getEditedTotal = () => {
    if (!purchaseOrder?.items) return 0;
    return purchaseOrder.items.reduce((sum, item) => sum + getEditedReceivedTotal(item), 0);
  };

  const canEditCosts = purchaseOrder?.financial_status?.toUpperCase() === 'UNPAID' && controlRole !== 'STAFF';

  const handleCopyPaymentCode = async () => {
    if (purchaseOrder?.payment_reference_code) {
      try {
        await navigator.clipboard.writeText(purchaseOrder.payment_reference_code);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const InfoCard = ({ icon, title, value, subtitle }) => (
    <Card sx={{ 
      height: '100%',
      borderRadius: '12px',
      boxShadow: `0 4px 12px rgba(14, 42, 4, 0.1)`,
      border: `1px solid rgba(107, 142, 78, 0.1)`,
      transition: 'transform 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 20px rgba(14, 42, 4, 0.15)`,
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography variant="h6" sx={{ 
            ml: 1, 
            color: brandColors.primary,
            fontWeight: 600,
            fontFamily: 'Poppins, Inter, system-ui, sans-serif',
            fontSize: '1rem'
          }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h5" sx={{ 
          color: brandColors.primary, 
          fontWeight: 700,
          fontFamily: 'Poppins, Inter, system-ui, sans-serif',
          mb: subtitle ? 1 : 0
        }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary" sx={{
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container component="main" maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="text" width={200} height={30} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Skeleton variant="rectangular" height={120} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Skeleton variant="rectangular" height={120} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Skeleton variant="rectangular" height={120} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Skeleton variant="rectangular" height={120} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="rectangular" height={400} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container component="main" maxWidth="xl">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/purchase-orders')}
          sx={{ color: brandColors.primary }}
        >
          Quay lại danh sách
        </Button>
      </Container>
    );
  }

  if (!purchaseOrder) {
    return (
      <Container component="main" maxWidth="xl">
        <Alert severity="warning" sx={{ mb: 3 }}>
          Không tìm thấy đơn đặt hàng.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/purchase-orders')}
          sx={{ color: brandColors.primary }}
        >
          Quay lại danh sách
        </Button>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xl">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/purchase-orders"
          sx={{ 
            color: brandColors.secondary,
            textDecoration: 'none',
            '&:hover': { color: brandColors.primary }
          }}
        >
          Đơn đặt hàng
        </Link>
        <Typography color="textPrimary" sx={{ fontWeight: 500 }}>
          {purchaseOrder.po_number}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography 
            component="h1" 
            variant="h4" 
            sx={{ 
              mb: 2,
              color: brandColors.primary,
              fontWeight: 700,
              fontFamily: 'Poppins, Inter, system-ui, sans-serif',
              background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {purchaseOrder.po_number}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {purchaseOrder.is_historical && (
              <Chip
                label="Đơn lịch sử"
                sx={{
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  fontWeight: 600,
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              />
            )}
            <StatusChip status={purchaseOrder.status} />
            <FinancialStatusChip status={purchaseOrder.financial_status} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/purchase-orders')}
            sx={{ 
              color: brandColors.secondary,
              borderColor: brandColors.secondary,
              '&:hover': { 
                borderColor: brandColors.primary,
                backgroundColor: `${brandColors.light}30`
              }
            }}
            variant="outlined"
          >
            Quay lại
          </Button>
          
          {purchaseOrder.status === 'DRAFT' && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => navigate(`/purchase-orders/${id}/edit`)}
              variant="outlined"
              sx={{
                borderColor: brandColors.secondary,
                color: brandColors.primary,
                '&:hover': {
                  borderColor: brandColors.primary,
                  backgroundColor: brandColors.light,
                }
              }}
            >
              Chỉnh sửa
            </Button>
          )}
          
          <Button
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${brandColors.secondary} 0%, ${brandColors.primary} 100%)`,
              }
            }}
          >
            In
          </Button>
          
          {/* Receiving button for Staff/Admin when PO is ready to receive */}
          {(purchaseOrder.status === 'SENT' || purchaseOrder.status === 'PARTIALLY_RECEIVED') && (
            <Button
              startIcon={<InventoryIcon />}
              onClick={() => navigate(`/purchase-orders/${id}/receiving`)}
              variant="contained"
              color="success"
              sx={{
                borderRadius: '12px',
                py: 1.5,
                px: 3,
                fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                fontWeight: 600,
                boxShadow: `0 4px 12px rgba(46, 125, 50, 0.3)`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: `0 6px 16px rgba(46, 125, 50, 0.4)`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Nhận hàng
            </Button>
          )}
          
          {/* Status action buttons */}
          {getStatusActionButton('SENT', 'Gửi cho nhà cung cấp')}
          {getStatusActionButton('CANCELLED', 'Hủy đơn', 'secondary')}
        </Box>
      </Box>

      {/* Historical PO Alert */}
      {purchaseOrder.is_historical && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: '12px',
            backgroundColor: 'rgba(158, 158, 158, 0.1)',
            '& .MuiAlert-icon': {
              color: '#9e9e9e'
            }
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            Đây là đơn hàng lịch sử
          </Typography>
          <Typography variant="body2">
            Đơn hàng này chỉ để ghi nhận lịch sử. Việc nhận hàng sẽ không ảnh hưởng đến số lượng tồn kho hiện tại.
          </Typography>
        </Alert>
      )}

      {/* Info Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {controlRole !== 'STAFF' && (
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              icon={<BusinessIcon sx={{ color: brandColors.secondary }} />}
              title="Nhà cung cấp"
              value={purchaseOrder.supplier?.name || 'N/A'}
              subtitle={purchaseOrder.supplier?.contact_email}
            />
          </Grid>
        )}
        
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard
            icon={<CalendarIcon sx={{ color: brandColors.secondary }} />}
            title="Ngày đặt"
            value={new Date(purchaseOrder.order_date).toLocaleDateString('vi-VN')}
            subtitle={`Tạo lúc ${new Date(purchaseOrder.created_at).toLocaleString('vi-VN')}`}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard
            icon={<ReceiptIcon sx={{ color: brandColors.secondary }} />}
            title="Số lượng sản phẩm"
            value={purchaseOrder.items?.length || 0}
            subtitle="sản phẩm"
          />
        </Grid>
        
        {controlRole !== 'STAFF' && (
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard
              icon={<MoneyIcon sx={{ color: brandColors.secondary }} />}
              title="Tổng tiền"
              value={`${(purchaseOrder.total_received_amount || 0).toLocaleString('vi-VN')} đ`}
              subtitle="theo thực nhận"
            />
          </Grid>
        )}
      </Grid>

      {/* Finance Information - Only for Admin when PO is paid */}
      {controlRole !== 'STAFF' && purchaseOrder.financial_status?.toUpperCase() === 'PAID' && (
        <Paper sx={{
          mb: 4,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08)`,
        }}>
          <Box sx={{
            p: 3,
            background: `linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)`,
            borderBottom: `2px solid #2e7d32`,
          }}>
            <Typography variant="h6" sx={{
              color: '#2e7d32',
              fontWeight: 600,
              fontFamily: 'Poppins, Inter, system-ui, sans-serif',
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                width: '4px',
                height: '24px',
                backgroundColor: '#2e7d32',
                borderRadius: '2px',
                marginRight: '12px'
              }
            }}>
              <AccountBalanceIcon sx={{ mr: 1 }} />
              Thông tin thanh toán
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {purchaseOrder.payment_reference_code && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                    Mã tham chiếu thanh toán
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{
                      fontWeight: 700,
                      color: '#2e7d32',
                      fontFamily: 'monospace',
                      letterSpacing: '0.05em'
                    }}>
                      {purchaseOrder.payment_reference_code}
                    </Typography>
                    <Tooltip title={copiedCode ? 'Đã sao chép!' : 'Sao chép mã'}>
                      <IconButton
                        onClick={handleCopyPaymentCode}
                        size="small"
                        sx={{
                          color: copiedCode ? '#2e7d32' : '#666',
                          '&:hover': { color: '#2e7d32' }
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              )}
              {purchaseOrder.paid_at && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                    Ngày thanh toán
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.primary }}>
                    {new Date(purchaseOrder.paid_at).toLocaleString('vi-VN')}
                  </Typography>
                </Grid>
              )}
              {purchaseOrder.paid_by && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                    Người xác nhận
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.primary }}>
                    {purchaseOrder.paid_by.name || purchaseOrder.paid_by.email || 'N/A'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Paper>
      )}

      {/* Line Items */}
      <Paper sx={{ 
        mb: 4,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08)`,
      }}>
        <Box sx={{
          p: 3,
          background: `linear-gradient(135deg, ${brandColors.background} 0%, #ffffff 100%)`,
          borderBottom: `2px solid ${brandColors.secondary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{
            color: brandColors.primary,
            fontWeight: 600,
            fontFamily: 'Poppins, Inter, system-ui, sans-serif',
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              width: '4px',
              height: '24px',
              backgroundColor: brandColors.primary,
              borderRadius: '2px',
              marginRight: '12px'
            }
          }}>
            Chi tiết sản phẩm
          </Typography>
          {canEditCosts && !isMobile && (
            <Box>
              {editingCosts ? (
                <>
                  <Tooltip title="Lưu">
                    <IconButton
                      onClick={handleSaveCosts}
                      disabled={savingCosts}
                      sx={{ color: '#2e7d32' }}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Hủy">
                    <IconButton
                      onClick={handleCancelEditCosts}
                      disabled={savingCosts}
                      sx={{ color: '#d32f2f' }}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Sửa đơn giá">
                  <IconButton
                    onClick={handleStartEditCosts}
                    sx={{ color: brandColors.secondary }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {isMobile ? (
          // Mobile card view
          <Box sx={{ p: 2 }}>
            {purchaseOrder.items?.map((item, index) => (
              <Card key={index} sx={{ 
                mb: 2, 
                borderRadius: '12px',
                border: `1px solid rgba(107, 142, 78, 0.1)` 
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ 
                    color: brandColors.primary, 
                    fontWeight: 600,
                    mb: 2,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif' 
                  }}>
                    {item.product?.name}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Số lượng:</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {item.quantity_ordered}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Đơn giá:</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {(item.unit_cost || 0).toLocaleString('vi-VN')} đ
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        <strong>Thành tiền:</strong>
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        color: brandColors.primary, 
                        fontWeight: 700,
                        fontFamily: 'Poppins, Inter, system-ui, sans-serif' 
                      }}>
                        {(item.received_total || 0).toLocaleString('vi-VN')} đ
                      </Typography>
                    </Grid>
                    
                    {/* Receiving info for mobile */}
                    {(item.quantity_received_total !== undefined || item.receiving_history) && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Đã nhận:</strong>
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {item.quantity_received_total || 0}
                            {item.variance !== undefined && (
                              <Chip
                                label={item.variance === 0 ? 'Đúng' : (item.variance > 0 ? `+${item.variance}` : `${item.variance}`)}
                                size="small"
                                sx={{ 
                                  ml: 1,
                                  backgroundColor: item.variance === 0 ? '#2e7d32' : (item.variance > 0 ? '#f57c00' : '#d32f2f'),
                                  color: 'white',
                                  fontWeight: 500,
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Lịch sử:</strong>
                          </Typography>
                          {item.receiving_history && item.receiving_history.length > 0 ? (
                            <Button
                              size="small"
                              onClick={() => toggleRowExpansion(item.product_id || index)}
                              startIcon={expandedRows.has(item.product_id || index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              sx={{ p: 0, minWidth: 'auto' }}
                            >
                              {item.receiving_history.length} lần
                            </Button>
                          ) : (
                            <Typography variant="body1">Chưa có</Typography>
                          )}
                        </Grid>
                        
                        {/* Mobile History Expansion */}
                        {expandedRows.has(item.product_id || index) && item.receiving_history && item.receiving_history.length > 0 && (
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" sx={{ 
                              fontWeight: 600, 
                              color: brandColors.primary, 
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <HistoryIcon fontSize="small" />
                              Lịch sử nhận hàng
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {item.receiving_history.map((history, histIndex) => (
                                <Box key={histIndex} sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 2,
                                  p: 1,
                                  backgroundColor: brandColors.background,
                                  borderRadius: '8px',
                                  border: `1px solid ${brandColors.light}`
                                }}>
                                  <Typography variant="body2" sx={{
                                    minWidth: '70px',
                                    fontWeight: 500,
                                    color: brandColors.primary
                                  }}>
                                    {new Date(history.date).toLocaleDateString('vi-VN')}
                                  </Typography>
                                  <Chip 
                                    label={`${history.quantity} kg`}
                                    size="small"
                                    color="success"
                                    sx={{ minWidth: '60px' }}
                                  />
                                  <Typography variant="body2" color="textSecondary" sx={{ flex: 1, fontSize: '0.8rem' }}>
                                    {history.description || 'Không có ghi chú'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Desktop table view
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>
                    Sản phẩm
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>
                    Số lượng
                  </TableCell>
                  {controlRole !== 'STAFF' && (
                    <>
                      <TableCell align="right" sx={{ 
                        fontWeight: 600, 
                        color: brandColors.primary,
                        fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                        fontSize: '0.95rem'
                      }}>
                        Đơn giá
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 600, 
                        color: brandColors.primary,
                        fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                        fontSize: '0.95rem'
                      }}>
                        Thành tiền
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center" sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>
                    Đã nhận
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>
                    Lịch sử
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrder.items?.map((item, index) => (
                  <React.Fragment key={index}>
                    <TableRow sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: 'rgba(245, 248, 243, 0.3)'
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(107, 142, 78, 0.05)',
                      }
                    }}>
                      <TableCell sx={{ 
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontWeight: 500 
                      }}>
                        {item.product?.name}
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontFamily: 'Inter, system-ui, sans-serif' 
                      }}>
                        {item.quantity_ordered}
                      </TableCell>
                      {controlRole !== 'STAFF' && (
                        <>
                          <TableCell align="right" sx={{
                            fontFamily: 'Inter, system-ui, sans-serif'
                          }}>
                            {editingCosts ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editedCosts[item.id] ?? item.unit_cost}
                                onChange={(e) => handleCostChange(item.id, e.target.value)}
                                inputProps={{
                                  min: 0,
                                  step: 1000,
                                  style: { textAlign: 'right', fontSize: '0.875rem' }
                                }}
                                sx={{
                                  width: 120,
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff'
                                  }
                                }}
                              />
                            ) : (
                              `${(item.unit_cost || 0).toLocaleString('vi-VN')} đ`
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{
                            fontFamily: 'Inter, system-ui, sans-serif',
                            fontWeight: 600,
                            color: brandColors.primary
                          }}>
                            {editingCosts
                              ? `${getEditedReceivedTotal(item).toLocaleString('vi-VN')} đ`
                              : `${(item.received_total || 0).toLocaleString('vi-VN')} đ`}
                          </TableCell>
                        </>
                      )}
                      <TableCell align="center" sx={{ 
                        fontFamily: 'Inter, system-ui, sans-serif' 
                      }}>
                        {item.quantity_received_total || 0}
                        {item.variance !== undefined && (
                          <Chip
                            label={item.variance === 0 ? 'Đúng' : (item.variance > 0 ? `+${item.variance}` : `${item.variance}`)}
                            size="small"
                            sx={{ 
                              ml: 1,
                              backgroundColor: item.variance === 0 ? '#2e7d32' : (item.variance > 0 ? '#f57c00' : '#d32f2f'),
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontFamily: 'Inter, system-ui, sans-serif' 
                      }}>
                        {item.receiving_history && item.receiving_history.length > 0 ? (
                          <Button
                            size="small"
                            onClick={() => toggleRowExpansion(item.product_id || index)}
                            startIcon={expandedRows.has(item.product_id || index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ minWidth: '32px', padding: '4px' }}
                          >
                            {item.receiving_history.length}
                          </Button>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Chưa có
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded History Row */}
                    {expandedRows.has(item.product_id || index) && item.receiving_history && item.receiving_history.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ backgroundColor: brandColors.background, py: 2 }}>
                          <Box sx={{ ml: 2 }}>
                            <Typography variant="subtitle2" sx={{ 
                              fontWeight: 600, 
                              color: brandColors.primary, 
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <HistoryIcon fontSize="small" />
                              Lịch sử nhận hàng - {item.product?.name}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {item.receiving_history.map((history, histIndex) => (
                                <Box key={histIndex} sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 2,
                                  p: 1,
                                  backgroundColor: 'white',
                                  borderRadius: '8px',
                                  border: `1px solid ${brandColors.light}`
                                }}>
                                  <Typography variant="body2" sx={{
                                    minWidth: '80px',
                                    fontWeight: 500,
                                    color: brandColors.primary
                                  }}>
                                    {new Date(history.date).toLocaleDateString('vi-VN')}
                                  </Typography>
                                  <Chip 
                                    label={`${history.quantity} kg`}
                                    size="small"
                                    color="success"
                                    sx={{ minWidth: '60px' }}
                                  />
                                  <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
                                    {history.description || 'Không có ghi chú'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                
                {/* Total row */}
                {controlRole !== 'STAFF' && (
                  <TableRow sx={{ 
                    borderTop: `2px solid ${brandColors.secondary}`,
                    backgroundColor: `${brandColors.background}50`
                  }}>
                    <TableCell colSpan={3} sx={{ 
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      color: brandColors.primary,
                      fontFamily: 'Poppins, Inter, system-ui, sans-serif'
                    }}>
                      Tổng cộng
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      color: editingCosts ? '#1565c0' : brandColors.primary,
                      fontFamily: 'Poppins, Inter, system-ui, sans-serif'
                    }}>
                      {editingCosts
                        ? `${getEditedTotal().toLocaleString('vi-VN')} đ`
                        : `${(purchaseOrder.total_received_amount || 0).toLocaleString('vi-VN')} đ`}
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Returns Section - Only for Admin */}
      {controlRole !== 'STAFF' && (
        <POReturnsSection
          purchaseOrderId={id}
          purchaseOrder={purchaseOrder}
          controlRole={controlRole}
          onReturnCreated={handleReturnCreated}
        />
      )}

      {/* Notes */}
      {purchaseOrder.notes && (
        <Paper sx={{ 
          p: 4,
          borderRadius: '16px',
          boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08)`,
          background: `linear-gradient(135deg, #ffffff 0%, ${brandColors.background} 100%)`,
        }}>
          <Typography variant="h6" sx={{ 
            mb: 2,
            color: brandColors.primary,
            fontWeight: 600,
            fontFamily: 'Poppins, Inter, system-ui, sans-serif',
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              width: '4px',
              height: '24px',
              backgroundColor: brandColors.primary,
              borderRadius: '2px',
              marginRight: '12px'
            }
          }}>
            Ghi chú
          </Typography>
          <Typography variant="body1" sx={{ 
            fontFamily: 'Inter, system-ui, sans-serif',
            lineHeight: 1.6,
            color: '#555'
          }}>
            {purchaseOrder.notes}
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default PurchaseOrderDetail;
